import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import VerifiedDoctorBadge from "@/components/common/VerifiedDoctorBadge";
import DoctorPhotoTile from "@/components/common/DoctorPhotoTile";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, MapPin, Star, Calendar, Buildings, WhatsappLogo, X } from "@phosphor-icons/react";
import { waLink } from "@/lib/wa";
// CITIES strip parked 2026-02-28 — pending proper SEO content / keyword audit.
// import { CITIES } from "@/data/cities";


/* iter100.317 → iter100.318 — Card-friendly location line.
   Prefer the structured `area` field from the /clinics payload when
   present (doctors who filled the new Area/Neighborhood input on their
   Profile → Contact section). Fall back to parsing the first comma
   segment of the free-form address for grandfathered rows that were
   created before the split. Always pairs with `city`, deduped when
   `area` is already the same as `city` (Karuna case). */
function formatCardLocation(area, address, city) {
    const cleanCity = (city || "").trim();
    const cleanArea = ((area || "").trim())
        || ((address || "").split(",")[0].trim());
    if (!cleanArea && !cleanCity) return "";
    if (!cleanArea) return cleanCity;
    if (!cleanCity) return cleanArea;
    if (cleanArea.toLowerCase() === cleanCity.toLowerCase()) return cleanCity;
    return `${cleanArea}, ${cleanCity}`;
}


export default function FindDoctor() {
    // `clinics` here is one row per active practice_location enriched with its
    // parent doctor — so a doctor running 3 branches gets 3 result cards. Each
    // card links to the doctor's booking page (Stage 1 — patient still picks
    // doctor on booking; per-branch slot routing comes later).
    const [clinics, setClinics] = useState([]);
    const [cities, setCities] = useState([]);
    const [city, setCity] = useState("all");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);

    const fetchClinics = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (city && city !== "all") params.city = city;
            if (q) params.q = q;
            const { data } = await api.get("/clinics", { params });
            setClinics(data);
        } finally {
            setLoading(false);
        }
    }, [city, q]);

    useEffect(() => {
        api.get("/doctors/cities").then((r) => setCities(r.data));
        fetchClinics();
    }, [fetchClinics]);

    /* iter100.12 — Live search-as-you-type. Debounce by 300ms so the backend
       isn't hammered per keystroke, but the patient sees results update
       immediately without needing to hit "Search". */
    useEffect(() => {
        const t = setTimeout(() => { fetchClinics(); }, 300);
        return () => clearTimeout(t);
    }, [q, fetchClinics]);

    return (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 lg:py-10" data-testid="find-doctor-page">
            {/* iter98.38 — Header tightened so the page no longer eats the
                full above-the-fold on a 15" laptop. Previously: py-12 lg:py-16
                + text-5xl H1 + mb-10 spacing pushed the first doctor card
                ~500px down. New rhythm: py-8 lg:py-10 + text-4xl ceiling +
                mb-6 → first row of cards visible without scroll @ ~768px. */}
            <div className="mb-6">
                <div className="overline text-primary mb-2">Provider search</div>
                <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                    Find a certified doctor near you.
                </h1>
                <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
                    All providers are board-certified doctors trained in Invisique™ treatment.
                </p>

                {/* City quick-links parked 2026-02-28 — pending proper SEO strategy. */}
            </div>

            <div className="sticky top-16 lg:top-20 z-40 bg-background/85 backdrop-blur py-3 border-b border-border/60 mb-6">
                <div className="flex flex-col md:flex-row gap-3">
                    <div className="flex-1 relative">
                        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by doctor, clinic, or specialization..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-10 pr-9 h-11 rounded-full"
                            data-testid="doctor-search-input"
                            autoFocus
                        />
                        {q && (
                            <button
                                type="button"
                                onClick={() => setQ("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-muted-foreground"
                                aria-label="Clear search"
                                data-testid="doctor-search-clear"
                            >
                                <X size={12} weight="bold" />
                            </button>
                        )}
                    </div>
                    <Select value={city} onValueChange={setCity}>
                        <SelectTrigger className="md:w-48 h-11 rounded-full" data-testid="city-filter-select">
                            <SelectValue placeholder="All cities" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All cities</SelectItem>
                            {cities.filter((c) => c && c.trim()).map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {/* Live results count — reassures the patient the search is working. */}
                {!loading && (q || city !== "all") && (
                    <div className="mt-2 text-xs text-muted-foreground" data-testid="doctor-search-count">
                        {clinics.length === 0
                            ? "No matches"
                            : `${clinics.length} match${clinics.length === 1 ? "" : "es"}`}
                        {q && <> for &ldquo;<span className="font-medium text-slate-700">{q}</span>&rdquo;</>}
                        {city !== "all" && <> in <span className="font-medium text-slate-700">{city}</span></>}
                    </div>
                )}
            </div>

            {loading && <div className="text-center py-12 text-muted-foreground">Loading clinics...</div>}

            {!loading && clinics.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">No clinics found. Try another city.</div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {clinics.map((c) => {
                    /* iter100.18 — Whole card is now a clickable Link so
                       patients can tap anywhere (photo / name / clinic
                       row) to land on the doctor's profile — not just the
                       small "View & book" button. The WhatsApp icon
                       stops propagation so it still deep-links WA without
                       navigating away. Accessibility: the Link itself
                       carries an aria-label and the inner button becomes
                       decorative but still gets a testid for existing
                       tests. */
                    const targetHref = c.landing_slug ? `/dr/${c.landing_slug}` : `/book/${c.doctor_id}`;
                    const waUrl = waLink(
                        c.doctor_whatsapp_phone,
                        `Hi ${c.doctor_name || "Doctor"}, I saw your profile on Invisique™ and would like to book an appointment.`
                    );
                    return (
                        <Link
                            key={c.id}
                            to={targetHref}
                            aria-label={`View ${c.doctor_name || "doctor"}'s profile and book`}
                            className="group bg-card rounded-2xl overflow-hidden bento-shadow relative block transition-all hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                            data-testid={`clinic-card-${c.id}`}
                        >
                            {/* iter100.256 — "PRIMARY" pill hidden on public listing (user
                                cleanup pass). The "primary" concept is still surfaced
                                internally in doctor-portal Practice Locations, but on the
                                patient-facing card it added visual noise for a signal
                                patients don't act on. */}
                            {/* DCI verified badge — top-right. Only renders on admin-
                                verified doctors (see VerifiedDoctorBadge). */}
                            <div className="absolute top-3 right-3 z-10">
                                <VerifiedDoctorBadge doctor={c} size="sm" className="bg-white/95 backdrop-blur shadow-sm" />
                            </div>
                            <DoctorPhotoTile
                                src={c.doctor_image}
                                name={c.doctor_name}
                                testid={`clinic-card-${c.id}-photo`}
                            />
                            <div className="p-5">
                                <div className="flex items-center justify-between mb-1 gap-2">
                                    <h3 className="font-display text-lg font-semibold truncate min-w-0 flex-1">{c.doctor_name}</h3>
                                    {/* iter100.255 — "NEW PARTNER" pill hidden on the public Find-a-Doctor
                                        surface (user decision — cleaner listing, no "new"-signal needed
                                        for patients). Admin views still show the pill for operational
                                        clarity. Doctors with an actual rating still show the star block. */}
                                    {c.doctor_rating ? (
                                        <div className="flex items-center gap-1 text-sm shrink-0">
                                            <Star size={14} weight="fill" className="text-yellow-500" />
                                            <span className="font-medium">{c.doctor_rating}</span>
                                        </div>
                                    ) : null}
                                </div>
                                <p className="text-sm text-primary font-medium mb-3">{c.doctor_specialization}</p>
                                <div className="text-xs text-muted-foreground space-y-1.5 mb-4">
                                    <div className="flex items-center gap-1.5"><Buildings size={14} /> {c.clinic_name}</div>
                                    <div className="flex items-start gap-1.5"><MapPin size={14} className="mt-0.5 shrink-0" /> <span>{formatCardLocation(c.area, c.address, c.city)}</span></div>
                                    {/* Path B Phase 4 — gracefully degrade when fee/years are null (new partner
                                        who hasn't completed the first-login wizard yet). Show whichever we have,
                                        in plain text — no fake "₹0 consultation" or "undefined+ yrs exp". */}
                                    {(c.consultation_fee || c.doctor_years_experience) && (
                                        <div>
                                            {c.consultation_fee ? <>₹{c.consultation_fee} consultation</> : null}
                                            {c.consultation_fee && c.doctor_years_experience ? " · " : null}
                                            {c.doctor_years_experience ? <>{c.doctor_years_experience}+ yrs exp</> : null}
                                        </div>
                                    )}
                                </div>
                                {/* iter66 — WhatsApp CTA alongside Book. Lets patients
                                    start a pre-filled "Hi Dr. ___" chat directly to the
                                    doctor's WhatsApp Business line. waLink() returns null
                                    when the doctor hasn't configured a usable number, in
                                    which case the icon button is suppressed entirely. */}
                                <div className="flex gap-2">
                                    {/* iter100.18 — With the entire card now clickable,
                                        the "View & book" CTA is styled as a strong visual
                                        anchor but no longer needs an inner Link (the
                                        parent Link handles navigation). The div-as-button
                                        renders identical UI without nesting <a> tags,
                                        which is invalid HTML. */}
                                    <span className="flex-1 inline-flex items-center justify-center h-9 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 group-hover:shadow-md transition-shadow" data-testid={`book-clinic-${c.id}-btn`}>
                                        <Calendar size={16} className="mr-2" /> View & book
                                    </span>
                                    {waUrl && (
                                        <a
                                            href={waUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e) => { e.stopPropagation(); }}
                                            aria-label={`WhatsApp ${c.doctor_name}`}
                                            className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white shadow-sm transition-colors"
                                            data-testid={`whatsapp-clinic-${c.id}-btn`}
                                            title={`Message ${c.doctor_name} on WhatsApp`}
                                        >
                                            <WhatsappLogo size={18} weight="fill" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
